/**
 * #813 step 4 — the builder's confirm card: wording + the canned USER turn a button posts.
 *
 * The card never answers for the user. A click sends a normal user message, so the server's
 * "did the user name this agent" gate passes on real user text:
 *  - update     → names the agent by label AND id (the gate matches either);
 *  - create_new → names NOTHING, so it can never unlock the existing agent.
 * Only the agent's label/id travel — never a message body or prompt.
 */
import type { AiChatItem, BuilderAsk } from '@common-components/ai-chat/transcript';

export type BuilderAskChoice = 'update' | 'create_new';

type AskT = (key: string, options?: { name?: string; id?: string }) => string;

export function askQuestionText(ask: BuilderAsk, t: AskT): string {
  if (ask.reason === 'duplicate_name') return t('myBridge.builderChat.askQuestionDuplicate', { name: ask.label });
  return t('myBridge.builderChat.askQuestionUnnamed', { name: ask.label });
}

export function askReplyText(ask: BuilderAsk, choice: BuilderAskChoice, t: AskT): string {
  if (choice === 'update') return t('myBridge.builderChat.askReplyUpdate', { name: ask.label, id: ask.agentId });
  if (ask.reason === 'duplicate_name') return t('myBridge.builderChat.askReplyCreateRenamed');
  return t('myBridge.builderChat.askReplyCreate');
}

/** The server stores an English fallback line on the ask turn; show the user's language instead. */
export function withAskText(items: AiChatItem[], t: AskT): AiChatItem[] {
  return items.map((it) => (it.kind === 'assistant' && it.ask ? { ...it, text: askQuestionText(it.ask, t) } : it));
}

/** The buttons are live only until the user answers: no user turn after the card. */
export function isAskActive(items: AiChatItem[], itemId: string): boolean {
  const i = items.findIndex((it) => it.id === itemId);
  return i >= 0 && !items.slice(i + 1).some((it) => it.kind === 'user');
}
